import React, { useState, useEffect, useRef } from 'react';
import Vapi from '@vapi-ai/web';

interface VapiWidgetProps {
  apiKey: string;
  assistantId: string;
  config?: Record<string, unknown>;
  onCallEndWithId: (callId: string) => void;
  textInputPlaceholder?: string;
}

const VapiWidget: React.FC<VapiWidgetProps> = ({ 
  apiKey, 
  assistantId,
  onCallEndWithId,
  textInputPlaceholder
}) => {
  const [vapi, setVapi] = useState<Vapi | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcript, setTranscript] = useState<Array<{role: string, text: string}>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const currentCallIdRef = useRef<string | null>(null);
  const onCallEndWithIdRef = useRef<typeof onCallEndWithId>();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const vapiInstance = new Vapi(apiKey);
    setVapi(vapiInstance);

    // Event listeners
    vapiInstance.on('call-start', () => {
      console.log('Call started');
      setIsConnected(true);
      setIsLoading(false);
    });

    vapiInstance.on('call-end', () => {
      console.log('Call ended');
      setIsConnected(false);
      setIsSpeaking(false);
      if (currentCallIdRef.current && onCallEndWithIdRef.current) {
        onCallEndWithIdRef.current(currentCallIdRef.current);
      }
    });

    vapiInstance.on('speech-start', () => {
      console.log('Assistant started speaking');
      setIsSpeaking(true);
    });

    vapiInstance.on('speech-end', () => {
      console.log('Assistant stopped speaking');
      setIsSpeaking(false);
    });

    vapiInstance.on('message', (message) => {
      console.log('Received message:', message);
      if (message.type === 'transcript') {
        setTranscript(prev => [...prev, {
          role: message.role,
          text: message.transcript,
          transcriptType: message.transcriptType
        }]);
      }
    });

    vapiInstance.on('error', (error) => {
      console.error('Vapi error:', error);
    });

    return () => {
      vapiInstance?.stop();
    };
  }, [apiKey]);

  useEffect(() => {
    onCallEndWithIdRef.current = onCallEndWithId;
  }, [onCallEndWithId]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [transcript]);

  const startCall = async () => {
    if (vapi) {
      setIsLoading(true);
      try {
        const call = await vapi.start(assistantId);
        if (call) {
          console.log("Call id:", call.id);
          currentCallIdRef.current = call.id;
        }
      } finally {
        //setIsLoading(false);
      }
    }
  };

  const endCall = () => {
    if (vapi) {
      vapi.stop();
    }
  };

  return (
    <div style={{
      position: 'relative',
      paddingTop: '20px',
      zIndex: 1000,
      fontFamily: 'Arial, sans-serif'
    }}>
      {!isConnected ? (
        <button
          onClick={startCall}
          disabled={isLoading}
          className="flex items-center justify-center w-full p-4 rounded-xl bg-[#42A48B] hover:bg-[#368c73] shadow-lg transform hover:scale-105 transition"
          style={{
            cursor: isLoading ? 'default' : 'pointer',
            transition: 'all 0.3s ease',
          }}
          onMouseOver={(e) => {
            if (!isLoading) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(18, 165, 148, 0.4)';
            }
          }}
          onMouseOut={(e) => {
            if (!isLoading) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(18, 165, 148, 0.3)';
            }
          }}
        >
          {isLoading ? (
            <><span className="loading-spinner mr-2" style={{
              display: 'inline-block',
              width: '22px',
              height: '22px',
              border: '3px solid #fff',
              borderTop: '3px solid #368c73',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              verticalAlign: 'middle',
            }} aria-label="Carregando" /> Carregando ...</>
          ) : textInputPlaceholder || "Iniciar a conversa"}
        </button>
      ) : (
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          width: '100%',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          border: '1px solid #e1e5e9'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: isSpeaking ? '#ff4444' : '#12A594',
                animation: isSpeaking ? 'pulse 1s infinite' : 'none'
              }}></div>
              <span style={{ fontWeight: 'bold', color: '#333' }}>
                {isSpeaking ? 'Professora IA falando...' : 'Escutando...'}
              </span>
            </div>
            <button
              onClick={endCall}
              style={{
                background: '#ff4444',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              Finalizar Conversa
            </button>
          </div>
          
          <div ref={messagesEndRef} style={{
            maxHeight: '200px',
            overflowY: 'auto',
            marginBottom: '12px',
            padding: '8px',
            background: '#f8f9fa',
            borderRadius: '8px'
          }}>
            {transcript.length === 0 ? (
              <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
                A conversa ja vai começar...
              </p>
            ) : (
              transcript.filter(msg => msg.transcriptType === 'diplaynothing').map((msg, i) => (
                <div
                  key={i}
                  style={{
                    marginBottom: '8px',
                    textAlign: msg.role === 'user' ? 'right' : 'left'
                  }}
                >
                  <span style={{
                    background: msg.role === 'user' ? '#12A594' : '#333',
                    color: '#fff',
                    padding: '8px 12px',
                    borderRadius: '12px',
                    display: 'inline-block',
                    fontSize: '14px',
                    maxWidth: '80%'
                  }}>
                    {msg.role === 'user' ? msg.text : "..."}
                  </span>
                </div>
              ))
            )}
          </div>

        </div>
      )}
      
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
        @keyframes spin {
          0% { transform: rotate(0deg);}
          100% { transform: rotate(360deg);}
        }
      `}</style>
    </div>
  );
};

export default VapiWidget;
